import { ReactNode, useEffect, useRef, useState } from 'react';

import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';

const collapsedWidthMobile = 122;
const collapsedWidthDesktop = 148;

export default function FloatPanel({
  isOpen,
  setIsOpen,
  title = 'Panel',
  placement = 'left',
  children,
  className,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title?: string;
  placement?: 'left' | 'right';
  children?: ReactNode;
  className?: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimeoutRef = useRef<number | null>(null);

  const isMobile = useMediaQuery('(max-width: 640px)');
  const collapsedWidth = isMobile
    ? collapsedWidthMobile
    : collapsedWidthDesktop;

  const [width, setWidth] = useState(collapsedWidth);
  // Measure content dimensions when they change
  useEffect(() => {
    if (!contentRef.current) return;

    const updateDimensions = () => {
      if (isOpen && contentRef.current) {
        const updatedWidth = Math.max(
          collapsedWidth,
          contentRef.current.offsetWidth,
        );
        setWidth(updatedWidth);
        setHeight(contentRef.current.scrollHeight);
      } else {
        setWidth(collapsedWidth);
        setHeight(0);
      }
    };

    // Initial measurement
    updateDimensions();

    // Watch for content size changes
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [collapsedWidth, isOpen]);

  // Trigger transitions when `isOpen` changes, regardless of source (header click or external)
  useEffect(() => {
    setIsTransitioning(true);
    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current);
    }
    transitionTimeoutRef.current = window.setTimeout(() => {
      setIsTransitioning(false);
      transitionTimeoutRef.current = null;
    }, 200);

    return () => {
      if (transitionTimeoutRef.current !== null) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, [isOpen]);

  return (
    <div
      className={`dark:bg-gray-90 pointer-events-auto absolute top-0 flex flex-col rounded-2xl bg-white px-3 ${placement === 'left' ? 'left-0' : 'right-0'} ${isOpen ? 'z-10' : 'z-9'} ${className}`}
      style={{
        minWidth: `${collapsedWidth}px`,
      }}
    >
      <header
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        role="button"
        className="flex h-[46px] items-center justify-between"
      >
        <Text
          variant="studio/body/medium/sm"
          sm:variant="studio/body/medium/md"
        >
          {title}
        </Text>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-180' : ''}`}
        />
      </header>
      <div
        className={
          isTransitioning ? 'transition-all duration-200 ease-in-out' : ''
        }
        style={{
          width: `${isOpen ? width : collapsedWidth}px`,
          height: `${height}px`,
          transitionDelay: isOpen ? '0ms' : '150ms', // Immediate transition-in, delay transition-out
        }}
      >
        <main
          ref={contentRef}
          className="min-w-fit pb-3 transition-opacity duration-200 ease-in-out"
          style={{
            opacity: isOpen ? 1 : 0,
            transitionDelay: isOpen ? '150ms' : '0ms', // Delay fade-in, immediate fade-out
            pointerEvents: isOpen ? 'auto' : 'none',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
